/**
 * Pinia store responsible for tenant module lifecycle state.
 *
 * The store bridges the static module registry in `apps/admin-center/modules`
 * with tenant-specific backend state. It answers questions such as which
 * modules are active, whether a module can go live, and which settings are
 * currently stored for the logged-in tenant.
 */
import { defineStore } from 'pinia'
import axiosInstance from '@/plugins/axios'
import { useSessionStore } from '@/store/sessionStore'

// Module base from DB
interface Module {
  _id: string
  key: string
  name: string
  description: string
  released: boolean
  enabledByDefault: boolean
}

// Module Status Type
type ModuleStatus = 'inactive' | 'configuring' | 'live'

// Tenant Module State (complete state including settings)
interface TenantModuleState {
  moduleKey: string
  status: ModuleStatus
  settings: Record<string, any>
  themeSettings: Record<string, any>
  flags: Record<string, boolean>
  lockedByAdmin: boolean
  validationState: {
    isValid: boolean
    missingFields: string[]
    errors: string[]
  }
  activatedAt?: string
  liveAt?: string
  lastConfiguredAt?: string
}

// Combined module info
interface ModuleInfo extends Module {
  status: ModuleStatus
  lockedByAdmin: boolean
  isConfigured: boolean
  validationState?: {
    isValid: boolean
    missingFields: string[]
    errors: string[]
  }
}

interface ModuleState {
  modules: Module[]
  moduleStates: Record<string, TenantModuleState>
  enabledModules: string[]
  loading: boolean
  error: string | null
  isLoaded: boolean
}

export const useModuleStore = defineStore('module', {
  state: (): ModuleState => ({
    modules: [],
    moduleStates: {},
    enabledModules: [],
    loading: false,
    error: null,
    isLoaded: false
  }),

  getters: {
    // Tenant ID helper
    tenantId: () => {
      const sessionStore = useSessionStore()
      return sessionStore.tenant?.id || (sessionStore.user as any)?.tenant
    },

    isModuleEnabled: (state) => (moduleKey: string) => {
      return state.enabledModules.includes(moduleKey)
    },

    getModule: (state) => (moduleKey: string) => {
      return state.modules.find((m) => m.key === moduleKey)
    },

    getModuleState: (state) => (moduleKey: string): TenantModuleState | undefined => {
      return state.moduleStates[moduleKey]
    },

    getModuleStatus: (state) => (moduleKey: string): ModuleStatus => {
      return state.moduleStates[moduleKey]?.status || 'inactive'
    },

    isModuleLive: (state) => (moduleKey: string) => {
      return state.moduleStates[moduleKey]?.status === 'live'
    },

    isModuleConfiguring: (state) => (moduleKey: string) => {
      return state.moduleStates[moduleKey]?.status === 'configuring'
    },

    // Check if module is active (configuring OR live)
    isModuleActive: (state) => (moduleKey: string) => {
      const status = state.moduleStates[moduleKey]?.status
      return status === 'configuring' || status === 'live'
    },

    canGoLive: (state) => (moduleKey: string) => {
      const moduleState = state.moduleStates[moduleKey]
      return moduleState?.status === 'configuring' && moduleState?.validationState?.isValid
    },

    releasedModules: (state) => {
      return state.modules.filter((m) => m.released)
    },

    isModuleLockedByAdmin: (state) => (moduleKey: string) => {
      return state.moduleStates[moduleKey]?.lockedByAdmin === true
    },

    // Combined info with status
    modulesWithStatus: (state): ModuleInfo[] => {
      return state.modules.map(m => ({
        ...m,
        status: state.moduleStates[m.key]?.status || 'inactive',
        lockedByAdmin: state.moduleStates[m.key]?.lockedByAdmin || false,
        isConfigured: state.moduleStates[m.key]?.validationState?.isValid || false,
        validationState: state.moduleStates[m.key]?.validationState
      }))
    },

    // Filter by status
    liveModules(): ModuleInfo[] {
      return this.modulesWithStatus.filter(m => m.status === 'live')
    },

    configuringModules(): ModuleInfo[] {
      return this.modulesWithStatus.filter(m => m.status === 'configuring')
    },

    inactiveModules(): ModuleInfo[] {
      return this.modulesWithStatus.filter(m => m.status === 'inactive')
    }
  },

  actions: {
    async loadModules() {
      this.loading = true
      this.error = null
      try {
        // Alle verfügbaren Module laden
        const response = await axiosInstance.get('/modules')
        this.modules = response.data
      } catch (error) {
        console.error('Error loading modules:', error)
        this.error = 'Fehler beim Laden der Module'
      } finally {
        this.loading = false
      }
    },

    async loadEnabledModules() {
      try {
        // Aktivierte Module für aktuellen Tenant laden
        const response = await axiosInstance.get('/modules/tenant')
        this.enabledModules = response.data.modules || []
      } catch (error) {
        console.error('Error loading enabled modules:', error)
      }
    },

    async loadModuleStates() {
      const tid = this.tenantId
      if (!tid) {
        console.warn('No tenant ID available for loading module states')
        return
      }

      try {
        const response = await axiosInstance.get(`/modules/tenant/${tid}/full`)
        const modulesWithState = response.data
        
        // Die API gibt { ...module, tenantState: { status, settings, ... } } zurück
        // Wir müssen das in unser Format konvertieren
        this.moduleStates = {}
        this.modules = []
        
        for (const moduleData of modulesWithState) {
          const { tenantState, manifest, ...moduleBase } = moduleData
          
          // Module-Basis speichern
          this.modules.push(moduleBase)
          
          // Tenant-Status speichern wenn vorhanden
          if (tenantState) {
            this.moduleStates[moduleBase.key] = {
              moduleKey: moduleBase.key,
              status: tenantState.status || 'inactive',
              lockedByAdmin: tenantState.lockedByAdmin || false,
              settings: tenantState.settings || {},
              themeSettings: tenantState.themeSettings || {},
              flags: tenantState.flags || {},
              validationState: tenantState.validationState || { isValid: false, missingFields: [], errors: [] },
              activatedAt: tenantState.activatedAt,
              liveAt: tenantState.liveAt,
              lastConfiguredAt: tenantState.lastConfiguredAt
            }
          }
        }

        // Update enabledModules based on states (exclude locked modules)
        this.enabledModules = Object.values(this.moduleStates)
          .filter(s => (s.status === 'configuring' || s.status === 'live') && !s.lockedByAdmin)
          .map(s => s.moduleKey)
          
        console.log('Module states loaded:', this.moduleStates)
        console.log('Enabled modules:', this.enabledModules)
      } catch (error) {
        console.error('Error loading module states:', error)
      }
    },

    async activateModule(moduleKey: string) {
      const tid = this.tenantId
      if (!tid) throw new Error('No tenant ID')

      try {
        const response = await axiosInstance.post(`/modules/tenant/${tid}/activate/${moduleKey}`)
        
        // Reload module states to get fresh data
        await this.loadModuleStates()
        
        return response.data
      } catch (error: any) {
        console.error('Error activating module:', error)
        throw new Error(error.response?.data?.message || 'Fehler beim Aktivieren des Moduls')
      }
    },

    async deactivateModule(moduleKey: string) {
      const tid = this.tenantId
      if (!tid) throw new Error('No tenant ID')

      try {
        const response = await axiosInstance.post(`/modules/tenant/${tid}/deactivate/${moduleKey}`)
        
        // Reload module states to get fresh data
        await this.loadModuleStates()
        
        return response.data
      } catch (error: any) {
        console.error('Error deactivating module:', error)
        throw new Error(error.response?.data?.message || 'Fehler beim Deaktivieren des Moduls')
      }
    },

    async goLiveWithModule(moduleKey: string) {
      const tid = this.tenantId
      if (!tid) throw new Error('No tenant ID')

      try {
        const response = await axiosInstance.post(`/modules/tenant/${tid}/golive/${moduleKey}`)
        
        // Reload module states to get fresh data
        await this.loadModuleStates()
        
        return response.data
      } catch (error: any) {
        console.error('Error going live with module:', error)
        throw new Error(error.response?.data?.message || 'Fehler beim Live-Schalten des Moduls')
      }
    },

    async updateModuleSettings(moduleKey: string, settings: Record<string, any>) {
      const tid = this.tenantId
      if (!tid) throw new Error('No tenant ID')

      try {
        const response = await axiosInstance.put(`/modules/tenant/${tid}/settings/${moduleKey}`, { settings })
        
        // Reload module states to get fresh data
        await this.loadModuleStates()
        
        return response.data
      } catch (error: any) {
        console.error('Error updating module settings:', error)
        throw new Error(error.response?.data?.message || 'Fehler beim Speichern der Einstellungen')
      }
    },

    async getModuleManifest(moduleKey: string) {
      try {
        const response = await axiosInstance.get(`/modules/manifest/${moduleKey}`)
        return response.data
      } catch (error: any) {
        console.error('Error loading module manifest:', error)
        return null
      }
    },

    async initialize() {
      if (this.isLoaded) return // Prevent double initialization
      
      await this.loadModuleStates()
      this.isLoaded = true
      
      console.log('ModuleStore initialized - modules:', this.modules.length, 'states:', Object.keys(this.moduleStates).length)
    },

    // Reset store (e.g., on logout)
    reset() {
      this.modules = []
      this.moduleStates = {}
      this.enabledModules = []
      this.loading = false
      this.error = null
      this.isLoaded = false
    }
  }
})
